@if (isActive()) {
  <section class="issue-list">
    <header class="issue-list__header">
      <div>
        <h2 class="issue-list__title">{{ i18n.t('issueList.title') }}</h2>
        <p class="issue-list__meta">{{ i18n.t('issueList.meta') }}</p>
      </div>

      <div
        class="app-view-toggle"
        role="tablist"
        [attr.aria-label]="i18n.t('issueList.viewMode.label')"
      >
        <button
          class="app-view-toggle__button"
          type="button"
          [class.app-view-toggle__button--active]="viewMode() === 'list'"
          [attr.aria-pressed]="viewMode() === 'list'"
          [attr.aria-label]="i18n.t('issueList.viewMode.list')"
          [attr.title]="i18n.t('issueList.viewMode.list')"
          (click)="setViewMode('list')"
        >
          <span class="fa-solid fa-table-list" aria-hidden="true"></span>
        </button>
        <button
          class="app-view-toggle__button"
          type="button"
          [class.app-view-toggle__button--active]="viewMode() === 'tiles'"
          [attr.aria-pressed]="viewMode() === 'tiles'"
          [attr.aria-label]="i18n.t('issueList.viewMode.tiles')"
          [attr.title]="i18n.t('issueList.viewMode.tiles')"
          (click)="setViewMode('tiles')"
        >
          <span class="fa-solid fa-table-cells-large" aria-hidden="true"></span>
        </button>
      </div>
    </header>

    @if (loadError(); as error) {
      <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
    }

    @if (isLoading()) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--loading fa-solid fa-spinner fa-spin" aria-hidden="true"></span> {{ i18n.t('issueList.states.loading') }}</div>
    } @else if (!visibleIssues().length) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('issueList.states.empty') }}</div>
    } @else if (viewMode() === 'list') {
      <div class="app-data-table-shell">
        <table class="app-data-table issue-list__table">
          <thead>
            <tr class="app-data-table__header-row">
              <th>{{ i18n.t('issueList.columns.title') }}</th>
              <th>{{ i18n.t('issueList.columns.location') }}</th>
              <th>{{ i18n.t('issueList.columns.priority') }}</th>
              <th>{{ i18n.t('issueList.columns.status') }}</th>
              <th>{{ i18n.t('issueList.columns.dueDate') }}</th>
              <th>{{ i18n.t('issueList.columns.reporter') }}</th>
              <th>{{ i18n.t('issueList.columns.attachments') }}</th>
              <th>{{ i18n.t('issueList.columns.updatedAt') }}</th>
              <th class="issue-list__actions-header">{{ i18n.t('issueList.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            @for (issue of visibleIssues(); track trackByIssueId($index, issue)) {
              <tr>
                <td class="issue-list__title-cell">
                  <strong>{{ issue.title }}</strong>
                  <span>{{ issue.description || i18n.t('issueList.fallbacks.noDescription') }}</span>
                </td>
                <td class="issue-list__location-cell">
                  <strong>{{ getLocationName(issue.location_id) }}</strong>
                  <span>{{ getRoleName(issue.assigned_role_id) }}</span>
                </td>
                <td>
                  <span class="issue-list__priority" [ngClass]="getPriorityClass(issue.priority)">
                    {{ getPriorityLabel(issue.priority) }}
                  </span>
                </td>
                <td>
                  <span class="app-status-chip" [ngClass]="getStatusClass(issue)">
                    {{ getStatusLabel(issue) }}
                  </span>
                </td>
                <td>
                  @if (issue.due_date) {
                    {{ issue.due_date | date: 'yyyy-MM-dd HH:mm' }}
                  } @else {
                    {{ i18n.t('issueList.fallbacks.noDueDate') }}
                  }
                </td>
                <td>{{ getReporterName(issue.reporter_user_code) }}</td>
                <td>{{ issue.attachments.length }}</td>
                <td class="issue-list__date-cell">
                  <strong>{{ issue.updated_at | date: 'yyyy-MM-dd HH:mm' }}</strong>
                  <span>{{ i18n.t('issueList.columns.createdAt') }}: {{ issue.created_at | date: 'yyyy-MM-dd HH:mm' }}</span>
                </td>
                <td class="issue-list__actions-cell">
                  <button
                    class="secondary-button secondary-button--compact secondary-button--indigo app-row-action-button"
                    type="button"
                    [attr.title]="i18n.t('issueList.actions.edit')"
                    [attr.aria-label]="i18n.t('issueList.actions.edit')"
                    (click)="editIssue(issue)"
                  >
                    <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="app-card-grid">
        @for (issue of visibleIssues(); track trackByIssueId($index, issue)) {
          <article class="app-data-card issue-list__card">
            <div class="app-data-card__header issue-list__card-header">
              <h3 class="issue-list__card-title">{{ issue.title }}</h3>
              <span class="app-status-chip" [ngClass]="getStatusClass(issue)">
                {{ getStatusLabel(issue) }}
              </span>
            </div>

            <p class="issue-list__card-description">
              {{ issue.description || i18n.t('issueList.fallbacks.noDescription') }}
            </p>

            <dl class="issue-list__details">
              <div>
                <dt>{{ i18n.t('issueList.columns.priority') }}</dt>
                <dd>
                  <span class="issue-list__priority" [ngClass]="getPriorityClass(issue.priority)">
                    {{ getPriorityLabel(issue.priority) }}
                  </span>
                </dd>
              </div>
              <div>
                <dt>{{ i18n.t('issueList.columns.location') }}</dt>
                <dd>{{ getLocationName(issue.location_id) }}</dd>
              </div>
              <div>
                <dt>{{ i18n.t('issueReport.fields.assignedRole') }}</dt>
                <dd>{{ getRoleName(issue.assigned_role_id) }}</dd>
              </div>
              <div>
                <dt>{{ i18n.t('issueList.columns.dueDate') }}</dt>
                <dd>
                  @if (issue.due_date) {
                    {{ issue.due_date | date: 'yyyy-MM-dd HH:mm' }}
                  } @else {
                    {{ i18n.t('issueList.fallbacks.noDueDate') }}
                  }
                </dd>
              </div>
              <div>
                <dt>{{ i18n.t('issueList.columns.attachments') }}</dt>
                <dd>{{ issue.attachments.length }}</dd>
              </div>
              <div>
                <dt>{{ i18n.t('issueList.columns.updatedAt') }}</dt>
                <dd>{{ issue.updated_at | date: 'yyyy-MM-dd HH:mm' }}</dd>
              </div>
              <div class="issue-list__details-wide">
                <dt>{{ i18n.t('issueList.columns.reporter') }}</dt>
                <dd>{{ getReporterName(issue.reporter_user_code) }}</dd>
              </div>
              <div>
                <dt>{{ i18n.t('issueList.columns.createdAt') }}</dt>
                <dd>{{ issue.created_at | date: 'yyyy-MM-dd HH:mm' }}</dd>
              </div>
            </dl>

            @if (issue.attachments.length) {
              <ul class="issue-list__attachment-names">
                @for (attachment of issue.attachments; track attachment.id) {
                  <li>
                    <span class="fa-solid fa-image" aria-hidden="true"></span>
                    <span>{{ attachment.original_name }}</span>
                  </li>
                }
              </ul>
            }

            <footer class="app-data-card__footer issue-list__card-footer">
              <button
                class="secondary-button secondary-button--compact secondary-button--indigo app-row-action-button"
                type="button"
                [attr.title]="i18n.t('issueList.actions.edit')"
                [attr.aria-label]="i18n.t('issueList.actions.edit')"
                (click)="editIssue(issue)"
              >
                <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
              </button>
            </footer>
          </article>
        }
      </div>
    }
  </section>
}
